import { useNominationForm } from '@percent/grants-nomination-form/hooks';
import { Loader } from '@percent/lemonade';
import { useNavigate, useParams } from 'react-router-dom';
import { NominationForm } from './NominationForm/NominationForm';
import { Layout } from '@percent/grants-nomination-form/ui';
import { useValidationSchema } from './hooks/useValidationSchema';
import { useForm, FormProvider } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect, useMemo } from 'react';
import { ClosedNominations } from '../ClosedNominations/ClosedNominations';
import { InvalidLink } from '../InvalidLink/InvalidLink';
import { isAfter } from 'date-fns';
import { fuseOperatingBudgetQuestions } from './NominationForm/utils/fuseOperatingBudgetQuestions';
import { EntryType } from '@percent/grants-nomination-form/api/nominationForm/nominationForm.types';

export const NominationFormContainer = () => {
  const { nominationFormData, isLoading, setNominationFormState, nominationFormState } = useNominationForm();
  const { pageId, programId } = useParams<{ pageId: string; programId: string }>();
  const navigate = useNavigate();

  const fusedFormData = useMemo(
    () => (nominationFormData ? fuseOperatingBudgetQuestions(nominationFormData) : nominationFormData),
    [nominationFormData],
  );

  // Array fields are fields that should be treateddifferently on form submission
  const { schema, arrayFieldsKeys } = useValidationSchema(fusedFormData);

  const methods = useForm({
    resolver: yupResolver(schema),
    reValidateMode: 'onChange',
    mode: 'onChange',
  });

  // Trigger validation every time the pageId or nominationFormData changes to disable the next button
  useEffect(() => {
    methods.trigger();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageId, nominationFormData]);

  useEffect(() => {
    if (nominationFormState === 'error') {
      navigate('/');
    }

    if (nominationFormState === 'submitted') {
      navigate(`/${programId}/confirmation`);
    }

    if (pageId && programId && nominationFormState !== 'dirty') {
      navigate(`/${programId}`);
    }
  }, [pageId, programId, nominationFormState, navigate]);

  if (isLoading || !fusedFormData) {
    return <Loader />;
  }

  if (fusedFormData.entryType === EntryType.INVITATION) {
    return <InvalidLink />;
  }

  if (fusedFormData.deadline && isAfter(new Date(), new Date(fusedFormData.deadline))) {
    return <ClosedNominations deadline={fusedFormData.deadline} programName={fusedFormData.programName} />;
  }

  return (
    <Layout logo={fusedFormData.logo} programName={fusedFormData.programName}>
      <FormProvider {...methods}>
        <NominationForm
          pageId={pageId ?? ''}
          nominationFormData={fusedFormData}
          arrayFieldsKeys={Array.from(arrayFieldsKeys)}
          setNominationFormState={setNominationFormState}
        />
      </FormProvider>
    </Layout>
  );
};
